import { ChatWithFeedbackBgWrapper } from '@modules/chatWithFeedback/components/ChatWithFeedbackBgWrapper';
import { ChatWithFeedbackInput } from '@modules/chatWithFeedback/components/ChatWithFeedbackInput';
import { ChatWithFeedbackSummaryContent } from '@modules/chatWithFeedback/components/ChatWithFeedbackSummaryContent';
import { ChatWithFeedbackSummaryFooterButtons } from '@modules/chatWithFeedback/components/ChatWithFeedbackSummaryFooterButtons';
import { WisdomErrorMessage } from '@modules/common/components/WisdomErrorMessage';
import { useWisdomPromptAutoCompleteOptions } from '@modules/common/hooks/useWisdomPromptAutoCompleteOptions';
import { useIsChatWithFeedbackEnabled } from '@modules/feed/hooks';
import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import { Box, Collapse, IconButton, Slide, Stack, alpha } from '@mui/material';
import { WebSocketMessage } from 'generated/graphql';
import { useCallback, useState } from 'react';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { WisdomProcessingStepsLoader } from '../../../common/components/WisdomProcessingStepsLoader';
import { trackFeedbackListSummaryEvents } from '../FeedbackListSummaryContainer/FeedbackListSummaryContainer';
import { FeedbackListSummaryEmptyState } from '../FeedbackListSummaryEmptyState';

interface ChatWithFeedbackSummaryContainerProps {
  shouldShowDefaultState: boolean;
  onGenerateSummaryClick: () => void;
  prompt?: string;
  onChangePrompt: (value?: string) => void;
  isSocketConnectionEstablished: string | null;
  isStopResponseInProgress: boolean;
  isGenerateActionPerformed: boolean;
  onStopSummaryGeneration: () => void;
  isLoading: boolean;
  isSummaryRendered: boolean;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  generateSummary: () => void;
  responseID: string | undefined;
  messages: WebSocketMessage[];
  queryContainer: QueryContainer;
  isCompleted: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  isError: boolean;
  shouldShowLoader: boolean;
  setShouldShowLoader: (shouldShowLoader: boolean) => void;
}

export const ChatWithFeedbackSummaryContainer = ({
  shouldShowDefaultState,
  onGenerateSummaryClick,
  prompt = '',
  onChangePrompt,
  isSocketConnectionEstablished,
  isStopResponseInProgress,
  isGenerateActionPerformed,
  onStopSummaryGeneration,
  isLoading,
  isSummaryRendered,
  isExpanded,
  setIsExpanded,
  generateSummary,
  responseID,
  queryContainer,
  messages,
  isCompleted,
  containerRef,
  isError,
  shouldShowLoader,
  setShouldShowLoader,
}: ChatWithFeedbackSummaryContainerProps) => {
  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();

  const [submittedPrompt, setSubmittedPrompt] = useState('');

  const hasNoSummary = messages.length === 0 && isCompleted;
  const isSummaryGenerationInProgress =
    !isCompleted && messages.length > 0 && isGenerateActionPerformed;

  const CWFAutoCompleteOptions = useWisdomPromptAutoCompleteOptions({
    queryContainer,
  });

  const onProgressComplete = useCallback(() => {
    setShouldShowLoader(false);
  }, [setShouldShowLoader]);

  const renderContent = () => {
    if (isLoading || shouldShowLoader) {
      return (
        <Stack mt={3} spacing={2}>
          <WisdomProcessingStepsLoader
            containerSxProps={{
              border: 0,
              background: 'unset',
              pl: 0,
            }}
            isLoadingComplete={!isLoading}
            onProgressComplete={onProgressComplete}
          />
        </Stack>
      );
    }

    if (isSummaryRendered) {
      return (
        <Collapse in={isExpanded} timeout="auto">
          <Stack spacing={2} mt={2}>
            <ChatWithFeedbackSummaryContent
              containerProps={{
                sx: {
                  maxHeight: 'calc(68vh - 200px)',
                  overflowY: 'auto',
                  px: 1,
                  pb: 2,
                },
              }}
              shouldDisablePredictionsTooltip
              hasNoSummary={hasNoSummary}
              openCitedArtefactsInNewTab
              isFeedbackSidebarView
            />
            <ChatWithFeedbackSummaryFooterButtons
              responseID={responseID}
              messages={messages}
              queryContainer={queryContainer}
              onCopySummary={(copiedSummaryText) => {
                trackFeedbackListSummaryEvents({
                  eventName:
                    'Feedback List Summary - Copy Summary of Summaries',
                  copiedSummaryText,
                });
              }}
              onRegenerate={
                !isSummaryGenerationInProgress
                  ? () => generateSummary()
                  : undefined
              }
              isFeedbackSidebarView
              viewToken={undefined}
            />
          </Stack>
        </Collapse>
      );
    }
  };

  if (!isChatWithFeedbackEnabled) {
    return null;
  }

  if (shouldShowDefaultState) {
    return (
      <Stack
        direction="row"
        width="100%"
        alignItems="center"
        justifyContent="center"
      >
        <FeedbackListSummaryEmptyState
          isLoading={isLoading}
          onSubmit={() => {
            onGenerateSummaryClick();
            setSubmittedPrompt(prompt);
          }}
          value={prompt}
          onChange={onChangePrompt}
          shouldShowLoader={
            !isSocketConnectionEstablished || isStopResponseInProgress
          }
          isSummaryGenerationInProgress={isSummaryGenerationInProgress}
          onStopSummaryGeneration={onStopSummaryGeneration}
          isFeedbackSidebarView
          submittedPrompt={submittedPrompt}
          autoCompleteProps={CWFAutoCompleteOptions}
        />
      </Stack>
    );
  }

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Slide
        in={!shouldShowDefaultState}
        direction="down"
        timeout={500}
        container={containerRef.current}
      >
        <Box>
          <ChatWithFeedbackBgWrapper
            containerSxProps={{
              pl: 3,
              pr: 1,
              py: 2,
            }}
          >
            <Stack direction="row" width="100%" alignItems="center">
              <ChatWithFeedbackInput
                onSubmit={() => {
                  onGenerateSummaryClick();
                  setSubmittedPrompt(prompt);
                }}
                value={prompt}
                onChange={onChangePrompt}
                shouldShowLoader={
                  !isSocketConnectionEstablished || isStopResponseInProgress
                }
                isSummaryGenerationInProgress={
                  isSummaryGenerationInProgress || isLoading
                }
                onStopSummaryGeneration={onStopSummaryGeneration}
                isFeedbackSidebarView
                submittedPrompt={submittedPrompt}
                isCompleted={isCompleted}
                autoCompleteProps={CWFAutoCompleteOptions}
              />
              <Box mt={-3} ml={1}>
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                  disabled={!isSummaryRendered}
                  sx={{
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette.misc?.hotKey.disabled ??
                          theme.palette.background.default,
                        0.1,
                      ),
                  }}
                >
                  {!isExpanded ? <ExpandMoreRounded /> : <ExpandLessRounded />}
                </IconButton>
              </Box>
            </Stack>
            <Box>{renderContent()}</Box>
            {isError && <WisdomErrorMessage sx={{ mt: 1 }} />}
          </ChatWithFeedbackBgWrapper>
        </Box>
      </Slide>
    </Box>
  );
};
